import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import inviteService from '@/services/inviteService';
import type { VerifyInviteResponseDto } from '@/services/inviteService';

export interface InviteState {
  accepting: boolean;
  success: boolean | null;
  message: string | null;
  verifying: boolean;
  verification: VerifyInviteResponseDto | null;
}

const initialState: InviteState = {
  accepting: false,
  success: null,
  message: null,
  verifying: false,
  verification: null,
};

export const verifyInvite = createAsyncThunk(
  'invite/verifyInvite',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await inviteService.verifyInvite(token);
      return response;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message ?? 'Failed to verify invite'
      );
    }
  }
);

export const acceptInvite = createAsyncThunk(
  'invite/acceptInvite',
  async (
    { token, password }: { token: string; password?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await inviteService.acceptInvite({ token, password });
      return response;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message ?? 'Failed to accept invite'
      );
    }
  }
);

const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    resetInviteState: (state) => {
      state.accepting = false;
      state.success = null;
      state.message = null;
      state.verifying = false;
      state.verification = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyInvite.pending, (state) => {
        state.verifying = true;
        state.message = null;
        state.verification = null;
      })
      .addCase(
        verifyInvite.fulfilled,
        (state, action: PayloadAction<VerifyInviteResponseDto>) => {
          state.verifying = false;
          state.verification = action.payload;
          state.message = action.payload.message ?? null;
        }
      )
      .addCase(verifyInvite.rejected, (state, action) => {
        state.verifying = false;
        state.verification = null;
        state.message = (action.payload as string) ?? 'Failed to verify invite';
      })
      .addCase(acceptInvite.pending, (state) => {
        state.accepting = true;
        state.success = null;
        state.message = null;
      })
      .addCase(
        acceptInvite.fulfilled,
        (
          state,
          action: PayloadAction<{ success: boolean; message: string }>
        ) => {
          state.accepting = false;
          state.success = action.payload.success;
          state.message = action.payload.message;
        }
      )
      .addCase(acceptInvite.rejected, (state, action) => {
        state.accepting = false;
        state.success = false;
        state.message = (action.payload as string) ?? 'Failed to accept invite';
      });
  },
});

export const { resetInviteState } = inviteSlice.actions;
export default inviteSlice.reducer;
